import { useEffect, useMemo, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { FACHRICHTUNG_OPTIONS } from '../lib/fachrichtung.js';
import { ANREDE_OPTIONS, ROLLE_OPTIONS } from '../lib/kontakt.js';

// Fester Titel-Katalog. Leerstring = kein Titel. Reihenfolge bewusst manuell,
// nicht alphabetisch — entspricht der gewünschten Auswahllogik (häufigste
// zuerst, danach Spezialfälle).
const TITEL_OPTIONS = [
  '',
  'Dr.',
  'Dr. med.',
  'Dr. med. dent.',
  'Dr. rer. nat.',
  'Prof. Dr.',
  'Prof. Dr. med.',
  'PD Dr.',
  'PD Dr. med.',
  'Dipl. med.',
];

const EMPTY = {
  anrede: '',
  titel: '',
  vorname: '',
  nachname: '',
  rolle: 'arzt',
  email: '',
  telefon: '',
  mobil: '',
  notizen: '',
  fachgebiet_neu: '',
};

export default function KontaktFormModal({ open, onClose, onSaved, praxisId, kontakt }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(kontakt ? { ...EMPTY, ...kontakt } : EMPTY);
      setError(null);
    }
  }, [open, kontakt]);

  // Falls der Kontakt einen Titel hat, der nicht im festen Katalog steht,
  // diesen Wert als zusätzliche Option anhängen — damit beim Bearbeiten
  // keine Daten verschwinden, wenn der User das Modal einfach öffnet und
  // wieder speichert.
  const titelOptionen = useMemo(() => {
    const original = (kontakt?.titel ?? '').trim();
    if (!original || TITEL_OPTIONS.includes(original)) return TITEL_OPTIONS;
    return [...TITEL_OPTIONS, original];
  }, [kontakt?.titel]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    // Anrede ist auf DB-Ebene optional (Altbestand-Migration), wird aber
    // im Frontend für neue und bearbeitete Kontakte verlangt. So wandern
    // bestehende Kontakte beim ersten Edit automatisch in den neuen
    // Standard und neue Datensätze landen nie ohne Anrede in der DB.
    if (!form.anrede) {
      setError('Bitte eine Anrede wählen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        praxis: praxisId,
        anrede: form.anrede,
        titel: form.titel,
        vorname: form.vorname,
        nachname: form.nachname,
        rolle: form.rolle,
        email: form.email,
        telefon: form.telefon,
        mobil: form.mobil,
        notizen: form.notizen,
        fachgebiet_neu: form.fachgebiet_neu || '',
      };
      let saved;
      if (kontakt?.id) {
        saved = await pb.collection('kontakte').update(kontakt.id, payload);
      } else {
        payload.angelegt_von = user?.id ?? null;
        saved = await pb.collection('kontakte').create(payload);
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kontakt?.id ? 'Kontakt bearbeiten' : 'Neuen Kontakt anlegen'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-3">
            <div>
              <label className="label">Anrede *</label>
              <select
                className="input mt-1"
                value={form.anrede}
                onChange={handleChange('anrede')}
              >
                <option value="">— bitte wählen —</option>
                {ANREDE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Titel</label>
              <select
                className="input mt-1"
                value={form.titel}
                onChange={handleChange('titel')}
              >
                {titelOptionen.map((t) => {
                  const istKatalog = TITEL_OPTIONS.includes(t);
                  const label = t === ''
                    ? '— kein Titel —'
                    : istKatalog
                    ? t
                    : `${t} (bisheriger Wert)`;
                  return (
                    <option key={t || '__leer'} value={t}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="label">Vorname</label>
              <input
                type="text"
                className="input mt-1"
                value={form.vorname}
                onChange={handleChange('vorname')}
              />
            </div>
          </div>

          <div>
            <label className="label">Nachname *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.nachname}
              onChange={handleChange('nachname')}
            />
          </div>

          <div>
            <label className="label">Rolle *</label>
            <select
              required
              className="input mt-1"
              value={form.rolle}
              onChange={handleChange('rolle')}
            >
              {ROLLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">E-Mail</label>
            <input
              type="email"
              className="input mt-1"
              value={form.email}
              onChange={handleChange('email')}
            />
          </div>

          <div>
            <label className="label">Telefon</label>
            <input
              type="tel"
              className="input mt-1"
              value={form.telefon}
              onChange={handleChange('telefon')}
            />
          </div>

          <div>
            <label className="label">Mobil</label>
            <input
              type="tel"
              className="input mt-1"
              value={form.mobil}
              onChange={handleChange('mobil')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Fachrichtung (strukturiert)</label>
            <select
              className="input mt-1"
              value={form.fachgebiet_neu}
              onChange={handleChange('fachgebiet_neu')}
            >
              <option value="">— nicht erfasst —</option>
              {FACHRICHTUNG_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Notizen</label>
            <textarea
              rows={4}
              className="input mt-1"
              value={form.notizen}
              onChange={handleChange('notizen')}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
